/**
 * VesselSpacesTab - Vessel spaces configuration tab
 */

import React from 'react';
import { Field, Label, Description } from '../../../catalyst/fieldset';
import { Switch } from '../../../catalyst/checkbox';

const VesselSpacesTab = ({ vesselConfig, updateConfig }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-base font-medium text-gray-900 mb-4">Vessel Spaces & Accommodation</h3>
      <div className="space-y-3">
        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Enclosed Spaces Present</Label>
              <Description>Vessel has enclosed spaces requiring entry procedures</Description>
            </div>
            <Switch
              checked={vesselConfig.hasEnclosedSpaces || vesselConfig.enclosed_spaces_present}
              onChange={(checked) => updateConfig({ hasEnclosedSpaces: checked, enclosed_spaces_present: checked })}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Wheelhouse/Pilot House</Label>
              <Description>Has wheelhouse or pilot house (counts as substantial enclosure)</Description>
            </div>
            <Switch
              checked={vesselConfig.hasWheelhouse}
              onChange={(checked) => updateConfig({ hasWheelhouse: checked })}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Accommodation Spaces (Sleeping/Living Quarters)</Label>
              <Description>
                Check if vessel has sleeping berths or dedicated living quarters.
                Note: Galleys are also accommodation spaces per WBC3, but can be tracked separately below.
              </Description>
            </div>
            <Switch
              checked={vesselConfig.hasAccommodation}
              onChange={(checked) => updateConfig({
                hasAccommodation: checked,
                hasCabin: checked,  // If has accommodation, likely has cabin
                enclosed_accommodation_present: checked
              })}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Machinery Space Present</Label>
              <Description>Vessel has dedicated machinery space (affects fire detection requirements)</Description>
            </div>
            <Switch
              checked={vesselConfig.hasMachinerySpace}
              onChange={(checked) => updateConfig({ hasMachinerySpace: checked })}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Galley Present</Label>
              <Description>
                Vessel has cooking facilities (affects fire equipment and detection requirements).
                Galleys are treated as accommodation spaces per WBC3.
              </Description>
            </div>
            <Switch
              checked={vesselConfig.hasGalley}
              onChange={(checked) => {
                if (!checked) {
                  // CRITICAL: When unchecking galley, clear ALL related fields
                  // These fields trigger FFE detectors in flattenVesselConfig.js

                  updateConfig({
                    hasGalley: false,
                    galleyHeatSource: null,
                    cookingType: null,  // Legacy field
                    hasCooking: false,
                    hasOpenFlame: false,
                    hasGasAppliance: false,
                    hasLiquidFuelAppliance: false,
                    gasAppliances: false,
                    openFlameProtection: {
                      blanket: false,
                      extraExtinguisher: false,
                      fixedSystem: false
                    },
                    fixedFFE: {
                      ...vesselConfig.fixedFFE,
                      galley: false
                    }
                  });
                } else {

                  updateConfig({ hasGalley: true });
                }
              }}
            />
          </div>
        </Field>

        {/* Galley as Accommodation Space info banner */}
        {vesselConfig.hasGalley && !vesselConfig.hasAccommodation && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Galley Treated as Accommodation Space
                </h3>
                <p className="mt-1 text-sm text-blue-700">
                  Under WBC3, galleys are classified as accommodation spaces (any enclosed space provided for use of persons on-board).
                  A <strong>fire detector will be required</strong> even though "Accommodation Spaces" above is unchecked.
                </p>
                <p className="mt-2 text-xs text-blue-600">
                  You can keep these settings separate for clarity - the fire detection requirements will apply correctly based on your galley configuration.
                </p>
              </div>
            </div>
          </div>
        )}

        {vesselConfig.hasGalley && (
          <>
            <Field>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Heat Source</Label>
                  <Description>Type of cooking equipment (affects fire and CO detector requirements)</Description>
                </div>
                <select
                  value={vesselConfig.galleyHeatSource || 'electric'}
                  onChange={(e) => {
                    const value = e.target.value;

                    const needsProtection = ['gas','open_flame','liquid_fuel'].includes(value);
                    updateConfig({
                      galleyHeatSource: value,
                      hasCooking: true,
                      hasOpenFlame: needsProtection,
                      hasGasAppliance: value === 'gas',
                      hasLiquidFuelAppliance: value === 'liquid_fuel',
                      ...(needsProtection ? {} : {
                        // If no open flame, ensure protections are cleared in form state
                        openFlameProtection: { blanket: false, extraExtinguisher: false, fixedSystem: false }
                      })
                    });
                  }}
                  className="rounded-md border-gray-300 text-sm"
                >
                  <option value="electric">Electric/Induction</option>
                  <option value="gas">Gas (LPG/Butane)</option>
                  <option value="liquid_fuel">Liquid Fuel (Diesel/Paraffin)</option>
                  {/* Legacy values removed from dropdown but still handled in logic */}
                </select>
              </div>
            </Field>

            {/* Open Flame Protection Options - show when gas, open_flame, or liquid_fuel selected */}
            {['gas', 'open_flame', 'liquid_fuel'].includes(vesselConfig.galleyHeatSource) && (
              <>
                {/* Info box explaining the requirement */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-blue-900">
                      <p className="font-medium">WBC3 16.3.1 Open Flame Requirement:</p>
                      <p className="mt-1">Every vessel with gas/open flame cooking must have:</p>
                      <ol className="mt-1 ml-4 list-decimal list-inside text-blue-800">
                        <li>One 5A/34B extinguisher (standard galley requirement - assumed present)</li>
                        <li>PLUS at least one of the additional protections below</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
              <Field>
                <div>
                  <Label>Additional open-flame protection installed:</Label>
                  <Description className="mb-3">
                    Select which additional protection method(s) are installed:
                  </Description>
                  <div className="space-y-2 ml-4">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={vesselConfig.openFlameProtection?.blanket || false}
                        onChange={(e) => updateConfig({ 
                          openFlameProtection: {
                            ...vesselConfig.openFlameProtection,
                            blanket: e.target.checked
                          }
                        })}
                        className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                      />
                      <span className="text-sm text-gray-700">Fire blanket</span>
                    </label>
                    
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={vesselConfig.openFlameProtection?.extraExtinguisher || false}
                        onChange={(e) => updateConfig({ 
                          openFlameProtection: {
                            ...vesselConfig.openFlameProtection,
                            extraExtinguisher: e.target.checked
                          }
                        })}
                        className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                      />
                      <span className="text-sm text-gray-700">Additional extinguisher (8A/68B) - beyond the standard 5A/34B</span>
                    </label>
                    
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={vesselConfig.openFlameProtection?.fixedSystem || false}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          // Update open flame protection choice
                          updateConfig({ 
                            openFlameProtection: {
                              ...vesselConfig.openFlameProtection,
                              fixedSystem: isChecked
                            },
                            // Also mirror to fixedFFE.galley (set both true and false)
                            fixedFFE: {
                              ...vesselConfig.fixedFFE,
                              galley: isChecked
                            }
                          });
                        }}
                        className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                      />
                      <span className="text-sm text-gray-700">Fixed galley suppression system (per 16.4)</span>
                    </label>
                  </div>
                </div>
              </Field>
              </>
            )}
          </>
        )}
      </div>
    </div>

    {/* Hazards & Risks Section */}
    <div>
      <h3 className="text-base font-medium text-gray-900 mb-4">Hazards & Risks</h3>
      <p className="text-sm text-gray-600 mb-4">Identify specific hazards that affect fire detection and suppression requirements</p>
      <div className="space-y-3">
        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Lithium Battery Systems</Label>
              <Description>Large lithium-ion battery installations (requires smoke, heat AND flame detectors)</Description>
            </div>
            <Switch
              checked={vesselConfig.hasLithiumBatteries}
              onChange={(checked) => updateConfig({ hasLithiumBatteries: checked })}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Concentrated Electrical Equipment</Label>
              <Description>Areas with significant electrical equipment concentration (requires fire detectors)</Description>
            </div>
            <Switch
              checked={vesselConfig.hasConcentratedElectrical}
              onChange={(checked) => updateConfig({ hasConcentratedElectrical: checked })}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Exhaust Leak Risk Areas</Label>
              <Description>Spaces where exhaust gases may accumulate (requires CO detectors)</Description>
            </div>
            <Switch
              checked={vesselConfig.hasExhaustLeakRisk}
              onChange={(checked) => updateConfig({ hasExhaustLeakRisk: checked })}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Gas Leak Risk Areas</Label>
              <Description>Spaces where flammable gas may leak (requires hydrocarbon gas detectors)</Description>
            </div>
            <Switch
              checked={vesselConfig.hasGasLeakRisk}
              onChange={(checked) => updateConfig({ hasGasLeakRisk: checked })}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Identified Fire Risk Areas</Label>
              <Description>Areas identified as increased fire risk (requires fire blankets)</Description>
            </div>
            <Switch
              checked={vesselConfig.hasFireRiskArea}
              onChange={(checked) => updateConfig({ hasFireRiskArea: checked })}
            />
          </div>
        </Field>

        <Field>
          <div className="flex items-center justify-between">
            <div>
              <Label>Gas Appliances</Label>
              <Description>Vessel has gas consuming appliances beyond cooking (affects detector requirements)</Description>
            </div>
            <Switch
              checked={vesselConfig.gasAppliances}
              onChange={(checked) => updateConfig({ gasAppliances: checked })}
            />
          </div>
        </Field>
      </div>
    </div>
  </div>
);

export default VesselSpacesTab;